import { useCallback, useState } from "react";
import {
  Columns3,
  FileCode2,
  PanelRightClose,
  Play,
  ScanEye,
} from "lucide-react";
import type { Citation } from "./CitationChip";
import { StreamdownChat } from "./slots";

export interface ArtifactPaneProps {
  artifactType?:
    | "citation"
    | "browser"
    | "tradingview"
    | "report"
    | "narrative";
  variant?: "pane" | "modal" | "sheet";
  tradingViewConfig?: { symbol: string; timeframe?: string };
  browserSessionId?: string;
  browserStatus?: "starting" | "active" | "closed";
  /** Live session URL for the Consul/Browserbase session, when one exists. */
  browserLiveUrl?: string;
  reportHtml?: string;
  reportMarkdown?: string;
  citationSource?: { title: string; url?: string; content?: string };
  narrativeCanvasId?: string;
  isBrowserUserControlling?: boolean;
  onBrowserTakeControl?: () => void;
  onBrowserResumeAgent?: () => void;
  onBegin?: () => void;
  onPinCitation?: (citation: Citation) => void;
  onClose: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}

export function ArtifactPane({
  artifactType,
  citationSource,
  reportHtml,
  reportMarkdown,
  tradingViewConfig,
  browserSessionId,
  browserStatus,
  browserLiveUrl,
  isBrowserUserControlling,
  onBrowserTakeControl,
  onBrowserResumeAgent,
  onBegin,
  onClose,
  width = 390,
  onWidthChange,
}: ArtifactPaneProps) {
  const [tab, setTab] = useState<"preview" | "diff" | "artifacts">("preview");
  const tabs = [
    { id: "preview" as const, Icon: ScanEye, label: "Preview" },
    { id: "diff" as const, Icon: FileCode2, label: "Diff" },
    { id: "artifacts" as const, Icon: Columns3, label: "Artifacts" },
  ];
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const startX = event.clientX;
      const startWidth = width;
      const onMove = (moveEvent: PointerEvent) => {
        const next = Math.min(
          720,
          Math.max(300, startWidth - (moveEvent.clientX - startX)),
        );
        onWidthChange?.(next);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [onWidthChange, width],
  );

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col border-l border-[var(--fintheon-accent)]/15 bg-[#070604]"
      style={{
        width,
      }}
    >
      <div
        className="absolute left-0 top-0 h-full w-1 cursor-col-resize"
        onPointerDown={handlePointerDown}
        title="Resize workbench"
      />
      <div className="flex items-center justify-between border-b border-[var(--fintheon-accent)]/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <Columns3 size={13} className="text-[var(--fintheon-accent)]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--fintheon-accent)]">
            Workbench
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onBegin && reportMarkdown && (
            <button
              type="button"
              onClick={onBegin}
              className="flex items-center gap-1 rounded border border-[var(--fintheon-accent)]/25 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/10"
              title="Begin this plan"
            >
              <Play size={11} />
              Begin
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--fintheon-text)]/30 transition-colors hover:text-[var(--fintheon-text)]/70"
            title="Close workbench"
          >
            <PanelRightClose size={14} />
          </button>
        </div>
      </div>
      <div className="flex border-b border-[var(--fintheon-accent)]/10">
        {tabs.map(({ id, Icon, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex flex-1 items-center justify-center gap-1.5 py-2 text-[10px] transition-colors ${
              tab === id
                ? "text-[var(--fintheon-accent)]"
                : "text-[var(--fintheon-text)]/35 hover:text-[var(--fintheon-text)]/65"
            }`}
          >
            <Icon size={11} />
            {label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {tab === "preview" && (
          <div className="space-y-3">
            {artifactType === "browser" ? (
              <BrowserArtifact
                sessionId={browserSessionId}
                status={browserStatus}
                liveUrl={browserLiveUrl}
                isUserControlling={isBrowserUserControlling}
                onTakeControl={onBrowserTakeControl}
                onResumeAgent={onBrowserResumeAgent}
              />
            ) : artifactType === "tradingview" ? (
              <TradingViewArtifact config={tradingViewConfig} />
            ) : (
              <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/30">
                  Active Preview
                </p>
                <p className="mt-2 text-[12px] leading-5 text-[var(--fintheon-text)]/65">
                  {artifactType
                    ? `Rendering ${artifactType} artifact.`
                    : "No live preview yet. Generated UI, browser screenshots, reports, and file previews will land here instead of being dumped into the chat thread."}
                </p>
              </div>
            )}
            {citationSource && (
              <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
                <p className="text-[12px] font-semibold text-[var(--fintheon-text)]/80">
                  {citationSource.title}
                </p>
                {citationSource.url && (
                  <a
                    href={citationSource.url}
                    className="mt-1 block truncate text-[10px] text-[var(--fintheon-accent)]/70"
                  >
                    {citationSource.url}
                  </a>
                )}
                {citationSource.content && (
                  <p className="mt-2 text-[11px] leading-5 text-[var(--fintheon-text)]/55">
                    {citationSource.content}
                  </p>
                )}
              </div>
            )}
            {reportHtml && (
              <div
                className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3 text-[12px] text-[var(--fintheon-text)]/70"
                dangerouslySetInnerHTML={{ __html: reportHtml }}
              />
            )}
            {reportMarkdown && (
              <div className="fintheon-chat-markdown rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3 text-[12px] text-[var(--fintheon-text)]/76">
                <StreamdownChat content={reportMarkdown} />
              </div>
            )}
          </div>
        )}
        {tab === "diff" && (
          <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
            <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/30">
              Diff
            </p>
            <p className="mt-2 text-[12px] leading-5 text-[var(--fintheon-text)]/55">
              File diffs and proposed patches should render here as inspectable
              artifacts before approval.
            </p>
          </div>
        )}
        {tab === "artifacts" && (
          <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
            <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/30">
              Artifacts
            </p>
            <p className="mt-2 text-[12px] leading-5 text-[var(--fintheon-text)]/55">
              Reports, citations, generated files, and tool outputs belong in
              this drawer, not as raw implementation text in the chat canvas.
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}

function StatusPill({ status }: { status?: "starting" | "active" | "closed" }) {
  const label =
    status === "active"
      ? "Live"
      : status === "starting"
        ? "Starting"
        : status === "closed"
          ? "Closed"
          : "No session";
  const tone =
    status === "active"
      ? "text-emerald-400"
      : status === "starting"
        ? "text-amber-400"
        : "text-[var(--fintheon-text)]/40";
  return (
    <span
      className={`text-[9px] font-semibold uppercase tracking-[0.12em] ${tone}`}
    >
      {label}
    </span>
  );
}

function BrowserArtifact({
  sessionId,
  status,
  liveUrl,
  isUserControlling,
  onTakeControl,
  onResumeAgent,
}: {
  sessionId?: string;
  status?: "starting" | "active" | "closed";
  liveUrl?: string;
  isUserControlling?: boolean;
  onTakeControl?: () => void;
  onResumeAgent?: () => void;
}) {
  const hasSession = Boolean(sessionId) || Boolean(liveUrl);

  if (!hasSession) {
    return (
      <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/30">
            Browser
          </p>
          <StatusPill status={status} />
        </div>
        <p className="mt-2 text-[12px] leading-5 text-[var(--fintheon-text)]/55">
          No active browser session. Consul opens a read-only session on demand
          — this pane mirrors it; it does not autonomously control your browser.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] px-3 py-2">
        <div className="flex items-center gap-2">
          <ScanEye size={12} className="text-[var(--fintheon-accent)]" />
          <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/45">
            {isUserControlling
              ? "You are driving"
              : "Consul session (read-only mirror)"}
          </span>
        </div>
        <StatusPill status={status} />
      </div>
      {liveUrl ? (
        <iframe
          title="Consul browser session"
          src={liveUrl}
          sandbox="allow-same-origin allow-scripts"
          className={`h-[360px] w-full rounded-md border border-[var(--fintheon-accent)]/10 bg-black ${
            isUserControlling ? "" : "pointer-events-none"
          }`}
        />
      ) : (
        <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3 text-[11px] leading-5 text-[var(--fintheon-text)]/55">
          Session{" "}
          <span className="text-[var(--fintheon-text)]/80">{sessionId}</span> is
          {status === "active" ? " active" : ` ${status ?? "pending"}`} but no
          live view URL is available yet.
        </div>
      )}
      {(onTakeControl || onResumeAgent) && (
        <div className="flex items-center gap-2">
          {onTakeControl && (
            <button
              type="button"
              onClick={onTakeControl}
              disabled={isUserControlling}
              className="rounded border border-[var(--fintheon-accent)]/25 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/10 disabled:opacity-40"
            >
              Take control
            </button>
          )}
          {onResumeAgent && (
            <button
              type="button"
              onClick={onResumeAgent}
              disabled={!isUserControlling}
              className="rounded border border-[var(--fintheon-accent)]/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--fintheon-text)]/55 transition-colors hover:text-[var(--fintheon-text)]/80 disabled:opacity-40"
            >
              Resume agent
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TradingViewArtifact({
  config,
}: {
  config?: { symbol: string; timeframe?: string };
}) {
  if (!config?.symbol) {
    return (
      <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
        <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/30">
          Chart
        </p>
        <p className="mt-2 text-[12px] leading-5 text-[var(--fintheon-text)]/55">
          No chart symbol attached yet.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[var(--fintheon-accent)]/10 bg-[#0a0905] p-3">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-semibold text-[var(--fintheon-text)]/85">
          {config.symbol}
        </span>
        {config.timeframe && (
          <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/40">
            {config.timeframe}
          </span>
        )}
      </div>
      <p className="mt-2 text-[11px] leading-5 text-[var(--fintheon-text)]/50">
        Chart context attached. Live levels are sourced from market data — not
        fabricated here.
      </p>
    </div>
  );
}
